import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { authAPI } from '../services/api';
import { AuthContext } from './AuthContext';
import {
  isKeycloakEnabled,
  initKeycloak,
  getKeycloakToken,
  keycloakLogout,
} from '../services/keycloak';

const decodeJwtPayload = (token) => {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const raw = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

// Role names are used as Keycloak issues them: the 11 personas and
// system_admin (backend/src/config/rbacModel.js). No aliases.
const normalizeRole = (value) => String(value || '').toLowerCase().trim();

const extractRolesFromToken = (token) => {
  const payload = decodeJwtPayload(token) || {};
  const directRoles = Array.isArray(payload.roles) ? payload.roles : [];
  const realmRoles = Array.isArray(payload?.realm_access?.roles)
    ? payload.realm_access.roles
    : [];
  const merged = [...directRoles, ...realmRoles]
    .map(normalizeRole)
    .filter(Boolean);
  return [...new Set(merged)];
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  // Set when Keycloak signed the user in but the Hub could not open their
  // session; without it the app just bounces back to the landing page.
  const [authError, setAuthError] = useState(null);

  /* ─── Session hydration from the Keycloak access token ─── */
  const hydrateFromToken = useCallback(async (token) => {
    if (!token) {
      setUser(null);
      setRoles([]);
      return;
    }
    localStorage.setItem('authToken', token);
    try {
      const response = await authAPI.getSession();
      const normalizedRoles = response?.user?.roles?.length
        ? response.user.roles.map(normalizeRole)
        : extractRolesFromToken(token);
      setUser(response.user);
      setRoles(normalizedRoles);
      setAuthError(null);
    } catch (error) {
      localStorage.removeItem('authToken');
      setUser(null);
      setRoles([]);
      setAuthError(error?.message || 'Failed to load your session');
    }
  }, []);

  /* ─── Boot: Keycloak SSO is the only way to authenticate ─── */
  useEffect(() => {
    const boot = async () => {
      const authenticated = await initKeycloak();
      if (authenticated) {
        const token = await getKeycloakToken();
        await hydrateFromToken(token);
      } else {
        // Also drops any leftover token from the retired local login.
        localStorage.removeItem('authToken');
        setUser(null);
        setRoles([]);
      }
      setLoading(false);
    };
    boot();
  }, [hydrateFromToken]);

  const signOut = async () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('walletAddress');
    localStorage.removeItem('activeTenantId');
    localStorage.removeItem('activePersona');
    setUser(null);
    setRoles([]);
    setAuthError(null);
    keycloakLogout();
  };

  const updateUser = (updates) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const refreshSession = async () => {
    const token = await getKeycloakToken();
    await hydrateFromToken(token);
  };

  const value = useMemo(
    () => ({
      user,
      roles,
      primaryRole: roles[0] || null,
      loading,
      authError,
      isKeycloak: isKeycloakEnabled,
      signOut,
      updateUser,
      refreshSession,
    }),
    [user, roles, loading, authError]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
